/* eslint-disable camelcase */
import { of, defer, EMPTY } from 'rxjs';
import { mergeMap, exhaustMap, catchError, filter, map, tap, share } from 'rxjs/operators';
import deepmerge from 'deepmerge';
import addAuthHeaders from '@clearscore/helpers.add-auth-headers';
import addClientInstance from '@clearscore/helpers.add-client-instance';
import addUrlVariables from '@clearscore/helpers.add-url-variables';
import sessionTypes from '@clearscore/constants.session-action-types';
import { setSessionCookie } from '@clearscore/helpers.cookies';
import { actions as sessionActions, selectors as sessionSelectors } from '@clearscore/redux.session';

let refreshTokenRequest;
const TIMEOUT_STATUS_CODES = [401, 403, 500];

function parseJwt(token) {
    return JSON.parse(
        decodeURIComponent(
            window
                .atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))
                .split('')
                .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
                .join(''),
        ),
    );
}

function createRequestAction(store, action) {
    const addAuth = addAuthHeaders(store);
    const addVars = addUrlVariables(store);

    return addVars(addAuth(addClientInstance(store, action)));
}

function fromRequestAction({ payload: { request = {}, meta } }, extendRequest) {
    if (extendRequest) return defer(() => meta.client(deepmerge(request, extendRequest)));
    return defer(() => meta.client(request));
}

function fromRefreshTokenRequestAction(requestAction) {
    if (refreshTokenRequest) return refreshTokenRequest;

    refreshTokenRequest = fromRequestAction(requestAction).pipe(
        share(),
        tap(() => {
            refreshTokenRequest = null;
        }),
    );

    return refreshTokenRequest;
}

/**
 * Check if action has correct structure in payload required for middleware
 * @param {object} action redux action
 * @returns {boolean} if action structure is correct
 */
function hasData(action) {
    return action?.payload?.request?.headers && action?.payload?.request?.url && action?.payload?.meta;
}

/**
 * Perform API request
 * @param {object} store redux store
 * @param {function} next next middleware func
 * @param {object} action redux action
 * @returns {void}
 */
function handleRequest(store, next, requestAction) {
    // dispatch original action type to reducer before attempting request
    next(requestAction);

    const {
        payload: { request = {} },
    } = requestAction;

    const dispatchSuccess = (response, requestData) =>
        store.dispatch({
            type: `${requestAction.type}_SUCCESS`,
            payload: response.data,
            requestData: requestData.data,
            requestMeta: requestData.meta,
            statusCode: response.status,
        });

    const dispatchError = ({ response }, requestData) =>
        store.dispatch({
            type: `${requestAction.type}_ERROR`,
            payload: response,
            requestMeta: requestData.meta,
            statusCode: response?.status,
        });

    const oAuthRequest$ = fromRequestAction(requestAction).pipe(
        catchError((requestError) =>
            of(requestError).pipe(
                filter(({ response: { status } }) => {
                    if (TIMEOUT_STATUS_CODES.includes(status)) return true;
                    throw requestError;
                }),
                map(() => {
                    const state = store.getState();
                    return createRequestAction(
                        store,
                        sessionActions.refreshToken({
                            refresh_token: state.session.auth.refresh_token,
                            device_id: parseJwt(sessionSelectors.getSessionToken(state))['device-id'],
                        }),
                    );
                }),
                exhaustMap((refreshTokenRequestAction) =>
                    fromRefreshTokenRequestAction(refreshTokenRequestAction).pipe(
                        catchError(() => {
                            store.dispatch(sessionActions.logout());
                            return EMPTY;
                        }),
                        map((res) => res.data),
                        tap((payload) => store.dispatch({ type: sessionTypes.LOGIN_OAUTH_REFRESH_SUCCESS, payload })),
                        tap(({ access_token, refresh_token }) =>
                            store.dispatch(
                                sessionActions.setNativeSession({
                                    access_token,
                                    refresh_token,
                                    refreshToken: refresh_token,
                                }),
                            ),
                        ),
                        tap(({ access_token, refresh_token }) =>
                            setSessionCookie({
                                access_token,
                                refresh_token,
                                market: store.getState().market.activeMarket,
                            }),
                        ),
                    ),
                ),
                mergeMap(({ access_token }) =>
                    fromRequestAction(requestAction, { headers: { Authorization: `Bearer ${access_token}` } }),
                ),
            ),
        ),
    );

    oAuthRequest$.subscribe(
        (response) => dispatchSuccess(response, request),
        (response) => dispatchError(response, request),
    );
}

/**
 * If action has correct signature, perform API request.
 * Dispatch success or error actions
 * @param {object} store redux store
 * @returns {Function} middleware
 */
const requestMiddleware = (store) => (next) => (action) => {
    // add request data from helpers
    const requestAction = createRequestAction(store, action);
    return hasData(requestAction) ? handleRequest(store, next, requestAction) : next(requestAction);
};

export default requestMiddleware;
